import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Link2, Music2, Apple, ShoppingBag, Twitter } from "lucide-react";
import Logo from "@/components/Logo";
import { useSite } from "@/context/SiteSettings";

const TikTokIcon = ({ size = 18 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
        <path d="M16.6 3c.4 2.2 1.9 3.7 4.1 4v3c-1.6 0-3-.5-4.1-1.3v6.6c0 3.5-2.6 6.2-6 6.2-3.3 0-5.9-2.6-5.9-5.9 0-3.4 2.8-6 6.3-5.7v3.1c-.3-.1-.6-.2-1-.2-1.6 0-2.8 1.2-2.8 2.8 0 1.5 1.2 2.7 2.7 2.7 1.6 0 2.8-1.2 2.8-2.9V3h3.9z" />
    </svg>
);

const ICONS = {
    instagram: Instagram,
    tiktok: TikTokIcon,
    youtube: Youtube,
    spotify: Music2,
    apple: Apple,
    amazon: ShoppingBag,
    audiomack: Music2,
    facebook: Facebook,
    x: Twitter,
    other: Link2,
};

const PLATFORM_LABELS = {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    spotify: "Spotify",
    apple: "Apple Music",
    amazon: "Amazon Music",
    audiomack: "Audiomack",
    facebook: "Facebook",
    x: "X",
};

const SocialIcon = ({ item }) => {
    const Icon = ICONS[item.platform] || Link2;
    const name = item.platform === "other" ? item.label || "Link" : PLATFORM_LABELS[item.platform];
    return (
        <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`social-link-${item.platform}`}
            aria-label={name}
            title={name}
            className="w-11 h-11 rounded-full border border-amber-500/25 flex items-center justify-center text-zinc-300 hover:text-black hover:bg-gradient-to-br hover:from-amber-400 hover:to-amber-600 hover:border-transparent hover:shadow-[0_0_20px_rgba(200,151,43,0.35)] transition-all duration-300"
        >
            <Icon size={18} />
        </a>
    );
};

const NAV = [
    { to: "/", label: "Home" },
    { to: "/music", label: "Music" },
    { to: "/videos", label: "Videos" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export const Footer = () => {
    const { socials, t } = useSite();
    return (
    <footer data-testid="site-footer" className="relative border-t border-amber-500/15 bg-[#07070B]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-3">
            <div>
                <div className="flex items-center">
                    <Logo variant="full" size={54} />
                </div>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-xs">{t("footer_tagline")}</p>
            </div>

            <div>
                <p className="font-meta text-[11px] tracking-[0.3em] uppercase text-amber-400/90 mb-5">Navigate</p>
                <ul className="space-y-3">
                    {NAV.map((n) => (
                        <li key={n.to}>
                            <Link
                                to={n.to}
                                data-testid={`footer-link-${n.label.toLowerCase()}`}
                                className="text-sm text-zinc-400 hover:text-amber-300 transition-colors duration-200"
                            >
                                {n.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            <div>
                <p className="font-meta text-[11px] tracking-[0.3em] uppercase text-amber-400/90 mb-5">Connect</p>
                <div className="flex flex-wrap items-center gap-3">
                    {(socials || []).map((item, i) => (
                        <SocialIcon key={`${item.platform}-${i}`} item={item} />
                    ))}
                </div>
            </div>
        </div>

        <div className="border-t border-white/5">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="font-meta text-[11px] tracking-[0.2em] uppercase text-zinc-600">
                    © {new Date().getFullYear()} ayevibemuzik. All rights reserved.
                </p>
            </div>
        </div>
    </footer>
    );
};

export default Footer;
